import { useState } from "react";
import {
  Check,
  Copy,
  Download,
  Film,
  Lock,
  Pause,
  Play,
  RefreshCw,
  Save,
  Share2,
  Trash2,
  Unlock,
  X
} from "lucide-react";
import {
  BODY_FONT_IDS,
  BODY_FONTS,
  DISPLAY_FONT_IDS,
  DISPLAY_FONTS,
  MOTIFS,
  OUTPUT_DIMENSIONS,
  PALETTES,
  STYLE_PRESETS,
  TEMPLATES,
  VIDEO_DIMENSIONS
} from "../constants";
import type {
  ExportProgress,
  SavedPreset,
  StudioState,
  TemplateId,
  VideoCapability
} from "../types";
import { Field, RangeControl, Section } from "./controls";

interface ControlPanelProps {
  state: StudioState;
  setState: (
    updater: (current: StudioState) => StudioState,
    historyKey?: string
  ) => void;
  locks: { palette: boolean; motif: boolean };
  onToggleLock: (key: "palette" | "motif") => void;
  onApplyTemplate: (template: TemplateId) => void;
  onRandomize: () => void;
  onShuffle: () => void;
  paused: boolean;
  onPause: () => void;
  onRestart: () => void;
  onExportPng: () => void;
  onExportVideo: () => void;
  capability: VideoCapability;
  progress: ExportProgress | null;
  exportActive: boolean;
  onCancelExport: () => void;
  exportError: string;
  presets: SavedPreset[];
  onSavePreset: (name: string) => void;
  onLoadPreset: (preset: SavedPreset) => void;
  onDeletePreset: (id: string) => void;
  onShare: () => void;
  onCopySeed: () => void;
}

function LockButton({
  locked,
  onClick,
  label
}: {
  locked: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      className={`lock-button ${locked ? "is-locked" : ""}`}
      onClick={onClick}
      aria-pressed={locked}
    >
      {locked ? <Lock size={14} aria-hidden="true" /> : <Unlock size={14} aria-hidden="true" />}
      {locked ? `${label} locked` : `Lock ${label.toLowerCase()}`}
    </button>
  );
}

export function ControlPanel({
  state,
  setState,
  locks,
  onToggleLock,
  onApplyTemplate,
  onRandomize,
  onShuffle,
  paused,
  onPause,
  onRestart,
  onExportPng,
  onExportVideo,
  capability,
  progress,
  exportActive,
  onCancelExport,
  exportError,
  presets,
  onSavePreset,
  onLoadPreset,
  onDeletePreset,
  onShare,
  onCopySeed
}: ControlPanelProps) {
  const [presetName, setPresetName] = useState("");
  const [showSave, setShowSave] = useState(false);
  const isExporting = exportActive || (progress !== null && progress.phase !== "done");
  const percent = progress ? Math.round((progress.currentFrame / progress.totalFrames) * 100) : 0;

  const patchText = (patch: Partial<StudioState["text"]>) =>
    setState(
      (current) => ({ ...current, text: { ...current.text, ...patch } }),
      `text:${Object.keys(patch).sort().join(",")}`
    );
  const patchStyle = (patch: Partial<StudioState["style"]>) =>
    setState(
      (current) => ({ ...current, style: { ...current.style, ...patch } }),
      `style:${Object.keys(patch).sort().join(",")}`
    );
  const patchTypography = (patch: Partial<StudioState["typography"]>) =>
    setState(
      (current) => ({
        ...current,
        typography: { ...current.typography, ...patch }
      }),
      `typography:${Object.keys(patch).sort().join(",")}`
    );
  const patchAnimation = (patch: Partial<StudioState["animation"]>) =>
    setState(
      (current) => ({
        ...current,
        animation: { ...current.animation, ...patch }
      }),
      `animation:${Object.keys(patch).sort().join(",")}`
    );

  const commitPreset = () => {
    const name = presetName.trim();
    if (!name) return;
    onSavePreset(name);
    setPresetName("");
    setShowSave(false);
  };
  const pngDimensions = OUTPUT_DIMENSIONS[state.output];
  const videoDimensions = VIDEO_DIMENSIONS[state.output];

  return (
    <aside className="control-rail" aria-label="Studio controls">
      <div className="rail-command">
        <button type="button" className="primary-command" onClick={onRandomize}>
          <RefreshCw size={17} aria-hidden="true" />
          Randomize visuals
        </button>
        <p>Rolls palette, motif, atmosphere, grain, and motion. Your copy stays untouched.</p>
      </div>

      <Section title="Templates" kicker={state.output === "wide" ? "16:9" : "1:1"} defaultOpen>
        <div className="template-list">
          {TEMPLATES.map((template) => (
            <button
              type="button"
              key={template.id}
              className={`template-button ${state.template === template.id ? "is-active" : ""}`}
              onClick={() => onApplyTemplate(template.id)}
              aria-pressed={state.template === template.id}
            >
              <span className={`aspect-mark ${template.output}`} aria-hidden="true" />
              <span>
                <strong>{template.label}</strong>
                <small>{template.description}</small>
              </span>
              {state.template === template.id ? <Check size={15} aria-hidden="true" /> : null}
            </button>
          ))}
        </div>
      </Section>

      <Section title="Text" kicker={state.text.align} defaultOpen>
        <div className="control-stack">
          <Field
            label="Eyebrow"
            value={state.text.eyebrow}
            maxLength={100}
            onChange={(eyebrow) => patchText({ eyebrow })}
          />
          <Field
            label="Headline"
            value={state.text.headline}
            maxLength={180}
            multiline
            onChange={(headline) => patchText({ headline })}
          />
          <Field
            label="Subline"
            value={state.text.subline}
            maxLength={220}
            multiline
            onChange={(subline) => patchText({ subline })}
          />
          <Field
            label="CTA"
            value={state.text.cta}
            maxLength={80}
            onChange={(cta) => patchText({ cta })}
          />

          <div className="split-control">
            <span>Alignment</span>
            <div className="segmented" role="group" aria-label="Text alignment">
              {(["left", "center", "right"] as const).map((align) => (
                <button
                  type="button"
                  key={align}
                  onClick={() => patchText({ align })}
                  aria-pressed={state.text.align === align}
                >
                  {align}
                </button>
              ))}
            </div>
          </div>

          <div className="split-control">
            <span>Text color</span>
            <div className="segmented" role="group" aria-label="Text color">
              {(["light", "dark"] as const).map((tone) => (
                <button
                  type="button"
                  key={tone}
                  onClick={() => patchText({ tone })}
                  aria-pressed={state.text.tone === tone}
                >
                  {tone}
                </button>
              ))}
            </div>
          </div>

          <RangeControl
            label="Horizontal position"
            value={state.text.positionX}
            min={0.05}
            max={0.95}
            step={0.01}
            onChange={(positionX) => patchText({ positionX })}
          />
          <RangeControl
            label="Vertical position"
            value={state.text.positionY}
            min={0.05}
            max={0.85}
            step={0.01}
            onChange={(positionY) => patchText({ positionY })}
          />
          <RangeControl
            label="Text width"
            value={state.text.width}
            min={0.25}
            max={0.9}
            step={0.01}
            onChange={(width) => patchText({ width })}
          />
        </div>
      </Section>

      <Section
        title="Typography"
        kicker={DISPLAY_FONTS[state.typography.displayFont].label}
      >
        <div className="font-controls">
          <span className="font-group-label">Headline typeface</span>
          <div className="font-choice-list" role="group" aria-label="Display font">
            {DISPLAY_FONT_IDS.map((fontId) => {
              const font = DISPLAY_FONTS[fontId];
              return (
                <button
                  type="button"
                  key={fontId}
                  onClick={() => patchTypography({ displayFont: fontId })}
                  aria-pressed={state.typography.displayFont === fontId}
                >
                  <span className="font-sample" style={{ fontFamily: font.family }}>
                    Aa
                  </span>
                  <strong>{font.label}</strong>
                  {state.typography.displayFont === fontId ? (
                    <Check size={14} aria-hidden="true" />
                  ) : null}
                </button>
              );
            })}
          </div>

          <span className="font-group-label">Supporting copy</span>
          <div className="segmented font-segmented" role="group" aria-label="Supporting font">
            {BODY_FONT_IDS.map((fontId) => {
              const font = BODY_FONTS[fontId];
              return (
                <button
                  type="button"
                  key={fontId}
                  style={{ fontFamily: font.family }}
                  onClick={() => patchTypography({ bodyFont: fontId })}
                  aria-pressed={state.typography.bodyFont === fontId}
                >
                  {font.label}
                </button>
              );
            })}
          </div>
        </div>
      </Section>

      <Section title="Style" kicker={PALETTES[state.style.palette].label} defaultOpen>
        <div className="section-heading-row">
          <span>Brand palette</span>
          <LockButton
            locked={locks.palette}
            label="Palette"
            onClick={() => onToggleLock("palette")}
          />
        </div>
        <div className="palette-grid">
          {STYLE_PRESETS.map((preset) => {
            const palette = PALETTES[preset.id];
            return (
              <button
                type="button"
                key={preset.id}
                className={state.style.palette === preset.id ? "is-active" : ""}
                onClick={() => {
                  patchStyle({
                    palette: preset.id,
                    motif: preset.motif,
                    ...preset.style
                  });
                }}
                aria-pressed={state.style.palette === preset.id}
              >
                <span
                  className="palette-swatch"
                  style={{
                    background: `linear-gradient(90deg, ${palette.colors.join(", ")})`
                  }}
                  aria-hidden="true"
                />
                {preset.label}
              </button>
            );
          })}
        </div>

        <div className="section-heading-row motif-heading">
          <span>Motif</span>
          <LockButton
            locked={locks.motif}
            label="Motif"
            onClick={() => onToggleLock("motif")}
          />
        </div>
        <div className="choice-wrap" role="group" aria-label="Visual motif">
          {MOTIFS.map((motif) => (
            <button
              type="button"
              key={motif.id}
              onClick={() => patchStyle({ motif: motif.id })}
              aria-pressed={state.style.motif === motif.id}
            >
              {motif.label}
            </button>
          ))}
        </div>

        <div className="control-stack compact-stack">
          <RangeControl
            label="Variance"
            value={state.style.variance}
            min={0}
            max={1}
            step={0.01}
            onChange={(variance) => patchStyle({ variance })}
          />
          <RangeControl
            label="Vibrance"
            value={state.style.vibrance}
            min={0.4}
            max={1.3}
            step={0.01}
            onChange={(vibrance) => patchStyle({ vibrance })}
            format={(value) => value.toFixed(2)}
          />
          <RangeControl
            label="Brightness"
            value={state.style.brightness}
            min={0.55}
            max={1.25}
            step={0.01}
            onChange={(brightness) => patchStyle({ brightness })}
            format={(value) => value.toFixed(2)}
          />
          <RangeControl
            label="Bokeh"
            value={state.style.bokeh}
            min={0}
            max={1}
            step={0.01}
            onChange={(bokeh) => patchStyle({ bokeh })}
          />
          <RangeControl
            label="Grain"
            value={state.style.grain}
            min={0}
            max={0.35}
            step={0.01}
            onChange={(grain) => patchStyle({ grain })}
          />
          <RangeControl
            label="Vignette"
            value={state.style.vignette}
            min={0}
            max={0.8}
            step={0.01}
            onChange={(vignette) => patchStyle({ vignette })}
          />
        </div>
      </Section>

      <Section title="Composition" kicker={`seed ${state.seed.toString(16).slice(0, 6)}`}>
        <button type="button" className="secondary-command" onClick={onShuffle}>
          <RefreshCw size={15} aria-hidden="true" />
          Shuffle composition
        </button>
        <p className="section-help">
          Changes geometry and movement while preserving your current palette, motif, and copy.
        </p>
        <button type="button" className="seed-button" onClick={onCopySeed}>
          <span>
            <small>Deterministic seed</small>
            <strong data-testid="seed-value">
              {state.seed.toString(16).padStart(8, "0").toUpperCase()}
            </strong>
          </span>
          <Copy size={15} aria-hidden="true" />
        </button>
      </Section>

      <Section title="Animation" kicker="10s · 60fps">
        <div className="transport">
          <button type="button" onClick={onPause}>
            {paused ? <Play size={15} aria-hidden="true" /> : <Pause size={15} aria-hidden="true" />}
            {paused ? "Play preview" : "Pause preview"}
          </button>
          <button type="button" onClick={onRestart}>
            <RefreshCw size={15} aria-hidden="true" />
            Restart loop
          </button>
        </div>
        <div className="control-stack compact-stack">
          <RangeControl
            label="Color drift"
            value={state.animation.colorDrift}
            min={0}
            max={1}
            step={0.01}
            onChange={(colorDrift) => patchAnimation({ colorDrift })}
          />
          <RangeControl
            label="Bokeh drift"
            value={state.animation.bokehDrift}
            min={0}
            max={1}
            step={0.01}
            onChange={(bokehDrift) => patchAnimation({ bokehDrift })}
          />
          <RangeControl
            label="Shape drift"
            value={state.animation.shapeDrift}
            min={0}
            max={1}
            step={0.01}
            onChange={(shapeDrift) => patchAnimation({ shapeDrift })}
          />
        </div>
      </Section>

      <Section title="Presets" kicker={`${presets.length} saved`}>
        {presets.length > 0 ? (
          <div className="saved-list">
            {presets.map((preset) => (
              <div key={preset.id}>
                <button type="button" onClick={() => onLoadPreset(preset)}>
                  <strong>{preset.name}</strong>
                  <small>
                    {preset.state.output === "wide" ? "16:9" : "1:1"} ·{" "}
                    {PALETTES[preset.state.style.palette].label}
                  </small>
                </button>
                <button
                  type="button"
                  className="icon-button"
                  onClick={() => onDeletePreset(preset.id)}
                  aria-label={`Delete ${preset.name}`}
                >
                  <Trash2 size={15} aria-hidden="true" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="empty-copy">Your named browser presets will appear here.</p>
        )}
        {showSave ? (
          <div className="inline-save">
            <input
              type="text"
              value={presetName}
              maxLength={60}
              autoFocus
              placeholder="Preset name"
              onChange={(event) => setPresetName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") commitPreset();
                if (event.key === "Escape") setShowSave(false);
              }}
            />
            <button
              type="button"
              className="icon-button"
              onClick={commitPreset}
              disabled={!presetName.trim()}
              aria-label="Save named preset"
            >
              <Check size={16} aria-hidden="true" />
            </button>
            <button
              type="button"
              className="icon-button"
              onClick={() => setShowSave(false)}
              aria-label="Cancel saving preset"
            >
              <X size={16} aria-hidden="true" />
            </button>
          </div>
        ) : (
          <button type="button" className="secondary-command" onClick={() => setShowSave(true)}>
            <Save size={15} aria-hidden="true" />
            Save current preset
          </button>
        )}
      </Section>

      <section className="export-block" aria-labelledby="export-title">
        <div className="export-title-row">
          <span className="export-resolution-list">
            <small>EXPORT</small>
            <strong id="export-title">
              {pngDimensions.width} × {pngDimensions.height} PNG
            </strong>
            <em>
              {videoDimensions.width} × {videoDimensions.height} VIDEO
            </em>
          </span>
          <button type="button" className="share-button" onClick={onShare}>
            <Share2 size={14} aria-hidden="true" />
            Share state
          </button>
        </div>

        {isExporting && progress ? (
          <div className="export-progress" aria-live="polite">
            <div>
              <span>
                {progress.phase === "finalizing" ? "Finalizing video" : `Rendering frame ${progress.currentFrame}`}
              </span>
              <strong>{percent}%</strong>
            </div>
            <progress max={100} value={percent} />
            <button type="button" onClick={onCancelExport}>
              Cancel export
            </button>
          </div>
        ) : (
          <div className="export-actions">
            <button type="button" onClick={onExportPng} disabled={isExporting}>
              <Download size={16} aria-hidden="true" />
              Export PNG
            </button>
            <button
              type="button"
              onClick={onExportVideo}
              disabled={isExporting || !capability.format}
              data-testid="video-export-button"
            >
              <Film size={16} aria-hidden="true" />
              Export {capability.format?.toUpperCase() ?? "video"}
            </button>
          </div>
        )}
        <p className="format-note">
          {capability.label} · {videoDimensions.width} × {videoDimensions.height}
        </p>
        {exportError ? (
          <p className="error-note" role="alert">
            {exportError}
          </p>
        ) : null}
      </section>
    </aside>
  );
}
